/**
 * Revenue Distributor - Core revenue distribution logic using backwards calculation
 *
 * This module implements the proven distribution logic from the engine-prototype:
 * - Backwards loop through cash flows to handle cascading interest obligations
 * - Revenue distribution helper map for managing bond allocations
 * - Principal distribution to bond projects based on capacity and targets
 * - Interest cascading to previous payment periods
 */

import {
  DISTRIBUTION_CONSTRAINTS,
  PRINCIPAL_AMOUNTS,
  TIME_PERIODS,
} from '~/config/financial-constants';
import { DistributionStrategyManager } from '~/engine/processing/engines/distribution-engine/distribution-strategy-manager';
import type { RevenueDistributionHelperMap } from '~/engine/processing/engines/distribution-engine/types';
import { InterestCalculationEngine } from '~/engine/processing/engines/interest-engine/interest-calculator';
import { capturePaymentState, DebugCapture } from '~/services/debug-system';
import type { PrincipalDecisionContext } from '~/services/debug-system/types';
import type { BondSeries, Candidate, CashFlow, Configuration, RevenueMap } from '~/types';
import { roundPrincipalDown } from '../../calculators/core/financial-math/rounding';
import type {
  BondAllocation,
  DistributionDebugCallback,
  DistributionPlan,
  MinConstraintResult,
  PaymentDistribution,
} from './distribution-types';

/**
 * Result from revenue availability calculation, including decision context.
 * Used to pass both the calculated value and debug context back to the caller.
 */
interface RevenueAvailableResult {
  /** Revenue available for principal distribution */
  revenueAvailable: number;
  /** Decision context capturing values at calculation time */
  decisionContext: PrincipalDecisionContext;
}

export class RevenueDistributor {
  /**
   * Main distribution function - implements backwards calculation to handle cascading interest
   *
   * Uses the pure calculate/apply pattern:
   * 1. calculateDistributionPlan() - Pure calculation, no side effects
   * 2. applyDistributionPlan() - Explicit mutation of candidate
   * 3. updateRevenueMapFromPlan() - Explicit mutation of revenue map
   *
   * @see calculateDistributionPlan for algorithm details
   */
  static distribute(candidate: Candidate, configuration: Configuration): void {
    const drfStartingBalance = parseFloat(configuration.drfInputs?.startingBalance || '0') || 0;

    // Create debug callback only if capturing this candidate
    // This check has near-zero overhead when not debugging
    const debugCallback: DistributionDebugCallback | undefined = DebugCapture.isCapturing(
      candidate.id
    )
      ? (params) => {
          capturePaymentState(
            candidate,
            params.cashFlow,
            params.paymentIndex,
            params.helperMap,
            configuration,
            params.decisionContext,
            params.bondAllocations
          );
        }
      : undefined;

    // Step 1: Pure calculation - the entire algorithm
    const plan = RevenueDistributor.calculateDistributionPlan(
      candidate.cashFlows,
      configuration.bondSeries,
      candidate.revenueMap,
      drfStartingBalance,
      debugCallback
    );

    // Step 2: Apply to candidate - explicit mutation
    RevenueDistributor.applyDistributionPlan(candidate, plan, configuration.bondSeries);

    // Step 3: Update revenue map - explicit mutation
    RevenueDistributor.updateRevenueMapFromPlan(candidate.revenueMap, plan);
  }

  // ============================================================================
  // PURE HELPER FUNCTIONS (Phase 3a - for use in Phase 3b algorithm)
  // ============================================================================

  /**
   * Finds the paired I/O payment for a Final payment using fiscal year matching.
   * Works for both summer tax (I/O at i-1) and winter tax (I/O at i+1).
   *
   * In summer tax, the array order is: I/O (Nov, index N), Final (May, index N+1)
   * In winter tax, the array order is: Final (May, index N), I/O (Nov, index N+1)
   *
   * This function searches adjacent indices to find the correct paired I/O
   * regardless of which comes first in the array.
   *
   * @param currentCf - The current cash flow (must be a Final payment to have a paired I/O)
   * @param cashFlows - All cash flows in the candidate
   * @param currentIndex - Index of current cash flow in the array
   * @returns The paired I/O cash flow and its index, or null if none found
   */
  private static findPairedIOPayment(
    currentCf: CashFlow,
    cashFlows: readonly CashFlow[],
    currentIndex: number
  ): { cf: CashFlow; index: number } | null {
    // Only Final payments have paired I/O payments
    if (currentCf.isInterestOnly) return null;

    const fiscalYearEnd = currentCf.relatedFiscalYearEndingDate;

    // Search adjacent indices for I/O with same fiscal year
    // Check i-1 first (summer tax pattern), then i+1 (winter tax pattern)
    for (const delta of [-1, 1]) {
      const idx = currentIndex + delta;
      if (idx >= 0 && idx < cashFlows.length) {
        const candidate = cashFlows[idx];
        if (candidate.isInterestOnly && candidate.relatedFiscalYearEndingDate === fiscalYearEnd) {
          return { cf: candidate, index: idx };
        }
      }
    }
    return null;
  }

  /**
   * Calculates the interest amount to cascade from one period to an earlier period.
   * Pure function - does not mutate any inputs.
   *
   * When principal is allocated in period N, the interest on that principal
   * must be paid in all periods 1 through N-1. This function calculates
   * how much interest cascades to a specific earlier period.
   *
   * @param principal - Principal amount that generated interest
   * @param interestRate - Annual interest rate for the bond
   * @param targetPaymentDate - Payment date of the target (earlier) period
   * @param series - Bond series configuration (for first payment proration)
   * @returns Interest amount to add to the target period's obligation
   * @pure Level 4 engine - pure calculation, no side effects
   */
  static calculateCascadeAmount(
    principal: number,
    interestRate: number,
    targetPaymentDate: string,
    series: BondSeries
  ): number {
    if (principal <= 0 || interestRate <= 0) {
      return 0;
    }

    // Calculate semi-annual interest
    let cascadeAmount = principal * (interestRate / 2);

    // Check if this is the first payment - apply proration
    if (targetPaymentDate === series.firstInterestPaymentDate) {
      const actualDays = InterestCalculationEngine.days360(
        series.issueDate,
        series.firstInterestPaymentDate
      );
      const standardDays = TIME_PERIODS.STANDARD_PERIOD_DAYS;
      const prorateRatio = actualDays / standardDays;

      cascadeAmount = Math.round(cascadeAmount * prorateRatio);
    }

    return Math.round(cascadeAmount * 100) / 100;
  }

  // ============================================================================
  // PURE DISTRIBUTION ALGORITHM (Phase 3b)
  // ============================================================================

  /**
   * Pure function that calculates the complete distribution plan using backwards
   * distribution with cascading interest.
   *
   * ## Algorithm Overview
   *
   * Municipal bonds require working BACKWARDS through payment periods because:
   * - Interest on principal allocated in period N must be paid in periods 1 to N-1
   * - By processing period 30 before period 29, we can cascade interest obligations
   *   to all earlier periods before they're processed
   *
   * ## Key Steps (per payment, from last to first)
   *
   * 1. **Calculate available revenue** - revenue minus existing debt minus cascaded obligations
   * 2. **Determine distributable principal** - apply constraints (min payment, targets)
   * 3. **Allocate to bonds** - distribute principal based on strategy
   * 4. **Cascade interest backwards** - add interest obligations to ALL earlier periods
   * 5. **Record distribution** - store allocation details for later application
   *
   * ## Purity Guarantee
   *
   * This function uses internal mutable state (Maps) but doesn't mutate any inputs,
   * making it a pure function:
   * - Same inputs always produce same outputs
   * - No side effects on caller's data
   * - Safe to call multiple times for comparison/preview
   *
   * ## Related
   *
   * @see backwards-distribution-algorithm.ts - Full algorithm documentation
   * @see applyDistributionPlan - Applies the plan to a candidate (explicit mutation)
   * @see updateRevenueMapFromPlan - Updates revenue map (explicit mutation)
   *
   * @param cashFlows - Readonly cash flow records (not mutated)
   * @param bondSeries - Bond series configuration with rates and targets
   * @param revenueMapInput - Revenue map (cloned internally, not mutated)
   * @param drfStartingBalance - DRF starting balance for minimum payment constraint
   * @returns DistributionPlan with all calculated distributions and obligations
   *
   * @pure Level 3 engine - deterministic algorithm, no side effects
   * @complexity O(n²) where n = number of payment periods (cascade to all earlier)
   */
  static calculateDistributionPlan(
    cashFlows: readonly CashFlow[],
    bondSeries: readonly BondSeries[],
    revenueMapInput: RevenueMap,
    drfStartingBalance: number,
    debugCallback?: DistributionDebugCallback
  ): DistributionPlan {
    // ========================================================================
    // INTERNAL STATE TRACKING (local mutability for pure function)
    // ========================================================================

    // Track total interest obligation per payment index
    const interestObligations = new Map<number, number>();

    // Track per-bond future interest obligations (bondId -> paymentIndex -> obligation)
    const bondFutureObligations = new Map<string, Map<number, number>>();

    // Initialize bond obligation maps
    for (const cf of cashFlows) {
      for (const bp of cf.bondPayments) {
        if (!bondFutureObligations.has(bp.bondSeriesId)) {
          bondFutureObligations.set(bp.bondSeriesId, new Map<number, number>());
        }
      }
    }

    // Track cumulative principal per bond (for target tracking)
    const cumulativePrincipal = new Map<string, number>();

    // Track cumulative DRF adjustment deficit from processed payments
    // Negative value means we've been consuming DRF buffer
    let cumulativeDrfAdjustment = 0;

    // Clone revenueMap for local modifications (maintains purity)
    const localRevenueMap: RevenueMap = JSON.parse(JSON.stringify(revenueMapInput));

    // Collect distributions (will be reversed at end for chronological order)
    const distributions: PaymentDistribution[] = [];
    let totalPrincipalAllocated = 0;

    // ========================================================================
    // BACKWARDS LOOP - Process from last payment to first
    // ========================================================================

    for (let i = cashFlows.length - 1; i >= 0; i--) {
      const cf = cashFlows[i];

      // Use fiscal year-aware pairing to find the paired I/O payment.
      // This works for both summer tax (I/O at i-1) and winter tax (I/O at i+1).
      const pairedIO = RevenueDistributor.findPairedIOPayment(cf, cashFlows, i);
      const nextCf = pairedIO?.cf ?? null;
      const nextCfIndex = pairedIO?.index ?? null;

      // Get current interest obligation for this payment (from previous iterations)
      const currentObligation = interestObligations.get(i) || 0;

      // Calculate availableForBonds using tracked obligation
      const currentAvailableForBonds = cf.revenueMinusExistingDebt - currentObligation;
      const nextAvailableForBonds =
        nextCf && nextCfIndex !== null
          ? nextCf.revenueMinusExistingDebt - (interestObligations.get(nextCfIndex) || 0)
          : 0;

      // Step 1: Determine available revenue for principal distribution
      const { revenueAvailable, decisionContext } =
        RevenueDistributor.calculateRevenueAvailablePure(
          cf,
          nextCf,
          nextCfIndex,
          bondSeries,
          cashFlows,
          i,
          drfStartingBalance,
          currentAvailableForBonds,
          nextAvailableForBonds,
          interestObligations,
          cumulativePrincipal,
          cumulativeDrfAdjustment
        );

      // Step 2: Build helper map for allocation decisions
      const helperMap = RevenueDistributor.buildHelperMapPure(
        cf,
        localRevenueMap,
        revenueAvailable,
        bondSeries,
        cumulativePrincipal,
        cashFlows,
        i
      );

      // Step 3: Calculate bond allocations
      const bondAllocations = RevenueDistributor.calculateBondAllocationsPure(
        cf,
        nextCf,
        bondSeries,
        helperMap,
        bondFutureObligations,
        i
      );

      // Calculate totals
      const totalBondPayment = bondAllocations.reduce((sum, a) => sum + a.totalPayment, 0);
      const periodPrincipal = bondAllocations.reduce((sum, a) => sum + a.principal, 0);
      totalPrincipalAllocated += periodPrincipal;

      // Track DRF adjustment for this payment
      // adjustment = revenue - bondPayment; negative means we're consuming DRF buffer
      const drfAdjustment = cf.revenueMinusExistingDebt - totalBondPayment;
      cumulativeDrfAdjustment += drfAdjustment;

      // Update cumulative principal tracking
      for (const alloc of bondAllocations) {
        const current = cumulativePrincipal.get(alloc.bondSeriesId) || 0;
        cumulativePrincipal.set(alloc.bondSeriesId, current + alloc.principal);
      }

      // Step 4: Cascade interest backwards to all previous payments
      for (let j = 0; j < i; j++) {
        if (!cashFlows[j].isActive) continue;

        RevenueDistributor.cascadeInterestPure(
          bondAllocations,
          cashFlows[j],
          j,
          bondSeries,
          interestObligations,
          bondFutureObligations
        );
      }

      // Step 5: Update local revenue map
      RevenueDistributor.updateRevenueMapPure(localRevenueMap, cf, bondAllocations);

      // Step 6: Debug capture (if callback provided)
      if (debugCallback) {
        try {
          debugCallback({
            paymentIndex: i,
            cashFlow: cf,
            helperMap,
            decisionContext,
            bondAllocations,
          });
        } catch (error) {
          // Don't let debug errors break distribution
          console.warn('[DebugCapture] Error in debug callback:', error);
        }
      }

      // Build PaymentDistribution for this period
      distributions.push({
        paymentIndex: i,
        paymentDate: cf.paymentDate,
        relatedTaxBillYear: cf.relatedTaxBillYear,
        revenueAvailable,
        bondAllocations,
        totalBondPayment,
      });
    }

    return {
      distributions: distributions.reverse(), // Return in chronological order
      interestObligations,
      totalPrincipalAllocated,
    };
  }

  /**
   * Pure version of revenue availability calculation.
   * Calculates how much revenue is available for principal distribution.
   * Returns both the result and a decision context for debug capture.
   */
  private static calculateRevenueAvailablePure(
    currentRecord: CashFlow,
    nextRecord: CashFlow | null,
    nextRecordIndex: number | null,
    bondSeries: readonly BondSeries[],
    cashFlows: readonly CashFlow[],
    currentIndex: number,
    drfStartingBalance: number,
    currentAvailableForBonds: number,
    nextAvailableForBonds: number,
    interestObligations: Map<number, number>,
    cumulativePrincipal: Map<string, number>,
    cumulativeDrfAdjustment: number
  ): RevenueAvailableResult {
    // Get interest obligations from tracking maps
    const currentInterestObligation = interestObligations.get(currentIndex) || 0;
    // Use fiscal year-aware index for paired I/O obligation
    const nextInterestObligation =
      nextRecord && nextRecordIndex !== null ? interestObligations.get(nextRecordIndex) || 0 : null;

    // Helper to build decision context with common values
    const buildDecisionContext = (
      maxInterestRate: number,
      interestDivisor: number,
      totalRevenueForDistribution: number,
      rawPrincipalCapacity: number,
      roundedPrincipalAvailable: number,
      minConstraintResult: MinConstraintResult | null = null
    ): PrincipalDecisionContext => ({
      // INPUTS
      currentRevenueMinusDebt: currentRecord.revenueMinusExistingDebt,
      currentInterestObligation,
      nextRevenueMinusDebt: nextRecord?.revenueMinusExistingDebt ?? null,
      nextInterestObligation,
      nextIsInterestOnly: nextRecord?.isInterestOnly ?? false,
      maxInterestRate,
      // COMPUTED VALUES
      currentAvailableForBonds,
      nextAvailableForBonds: nextRecord ? nextAvailableForBonds : null,
      totalRevenueForDistribution,
      interestDivisor,
      rawPrincipalCapacity,
      // RESULT
      roundedPrincipalAvailable,
      // MIN CONSTRAINT DEBUG INFO
      minConstraint: minConstraintResult?.constraint ?? null,
      minAvailable: minConstraintResult?.minAvailable ?? null,
      minAvailablePaymentIndex: minConstraintResult?.minPaymentIndex ?? null,
      minAvailablePaymentDate: minConstraintResult?.minPaymentDate ?? null,
      drfStartingBalanceUsed: minConstraintResult?.drfStartingBalance ?? null,
      projectedDrfAtConstraint: minConstraintResult?.projectedDrfAtConstraint ?? null,
      wasConstrainedByMinPayment:
        minConstraintResult !== null && minConstraintResult.constraint < rawPrincipalCapacity,
      // METADATA
      paymentDate: currentRecord.paymentDate,
      nextPaymentDate: nextRecord?.paymentDate ?? null,
      isInterestOnly: currentRecord.isInterestOnly,
    });

    // If interest-only, no principal
    if (currentRecord.isInterestOnly) {
      return {
        revenueAvailable: 0,
        decisionContext: buildDecisionContext(0, 1, 0, 0, 0),
      };
    }

    // Find maximum interest rate among active bonds
    const maxInterestRate = currentRecord.bondPayments.reduce((max, bp) => {
      if (!bp.isActive) return max;
      const series = bondSeries.find((bs) => bs.id === bp.bondSeriesId);
      if (series) {
        const rate = InterestCalculationEngine.getInterestRateForPayment(
          series,
          currentRecord.paymentDate
        );
        return Math.max(max, rate);
      }
      return max;
    }, 0);

    // Calculate total revenue available
    const totalRevenue = nextRecord?.isInterestOnly
      ? currentAvailableForBonds + nextAvailableForBonds
      : currentAvailableForBonds;

    // Calculate interest divisor
    const interestDivisor = nextRecord?.isInterestOnly
      ? 1 + maxInterestRate
      : 1 + maxInterestRate / 2;

    if (totalRevenue <= 0) {
      return {
        revenueAvailable: 0,
        decisionContext: buildDecisionContext(maxInterestRate, interestDivisor, totalRevenue, 0, 0),
      };
    }

    // Calculate raw principal
    const rawPrincipal = totalRevenue / interestDivisor;

    // Check for first payment of targeted bond
    const isFirstPaymentTargeted = RevenueDistributor.isFirstPaymentForTargetedBondPure(
      currentRecord,
      cashFlows,
      currentIndex,
      bondSeries
    );

    let constrainedPrincipal: number;
    let minConstraintResult: MinConstraintResult | null = null;

    if (isFirstPaymentTargeted) {
      // Calculate target gap for first payment (only for bonds actually at their first payment)
      constrainedPrincipal = RevenueDistributor.calculateTargetGapPure(
        currentRecord,
        bondSeries,
        cumulativePrincipal,
        cashFlows,
        currentIndex
      );
    } else {
      // Apply minimum payment constraint using tracked obligations
      minConstraintResult = RevenueDistributor.calculateMinConstraintPure(
        cashFlows,
        currentIndex,
        drfStartingBalance,
        maxInterestRate,
        interestObligations,
        cumulativeDrfAdjustment
      );
      constrainedPrincipal = Math.min(rawPrincipal, minConstraintResult.constraint);
    }

    const roundedResult = roundPrincipalDown(constrainedPrincipal);

    return {
      revenueAvailable: roundedResult,
      decisionContext: buildDecisionContext(
        maxInterestRate,
        interestDivisor,
        totalRevenue,
        rawPrincipal,
        roundedResult,
        minConstraintResult
      ),
    };
  }

  /**
   * Pure check for first payment of targeted bond.
   */
  private static isFirstPaymentForTargetedBondPure(
    currentRecord: CashFlow,
    cashFlows: readonly CashFlow[],
    currentIndex: number,
    bondSeries: readonly BondSeries[]
  ): boolean {
    for (const bp of currentRecord.bondPayments) {
      if (!bp.isActive) continue;

      const bond = bondSeries.find((bs) => bs.id === bp.bondSeriesId);
      if (!bond?.targetInformation?.principalTargetAmount) continue;

      // Check if there's an earlier non-I/O payment for this bond
      const hasEarlierPayment = cashFlows.slice(0, currentIndex).some((cf) => {
        const earlierBp = cf.bondPayments.find((p) => p.bondSeriesId === bp.bondSeriesId);
        return earlierBp?.isActive && !cf.isInterestOnly;
      });

      if (!hasEarlierPayment) {
        return true;
      }
    }
    return false;
  }

  /**
   * Pure calculation of target gap for first payment.
   * Only includes bonds that are ACTUALLY at their first payment.
   */
  private static calculateTargetGapPure(
    currentRecord: CashFlow,
    bondSeries: readonly BondSeries[],
    cumulativePrincipal: Map<string, number>,
    cashFlows: readonly CashFlow[],
    currentIndex: number
  ): number {
    let totalGap = 0;

    for (const bp of currentRecord.bondPayments) {
      if (!bp.isActive) continue;

      const bond = bondSeries.find((bs) => bs.id === bp.bondSeriesId);
      if (!bond?.targetInformation?.principalTargetAmount) continue;

      // Check if this specific bond is at its first payment
      const hasEarlierPayment = cashFlows.slice(0, currentIndex).some((cf) => {
        const earlierBp = cf.bondPayments.find((p) => p.bondSeriesId === bp.bondSeriesId);
        return earlierBp?.isActive && !cf.isInterestOnly;
      });

      // Only add gap for bonds that are at their first payment
      if (!hasEarlierPayment) {
        const targetAmount = bond.targetInformation.principalTargetAmount;
        const allocated = cumulativePrincipal.get(bp.bondSeriesId) || 0;
        totalGap += Math.max(0, targetAmount - allocated);
      }
    }

    return totalGap;
  }

  /**
   * Pure calculation of minimum payment constraint.
   * Returns constraint value along with debug info about which payment was limiting.
   *
   * Uses fiscal year grouping to combine payments that share the same fiscal year.
   * This correctly handles both Summer and Winter tax schedules where I/O payments
   * may come before or after the principal payment in the fiscal year.
   */
  private static calculateMinConstraintPure(
    cashFlows: readonly CashFlow[],
    currentIndex: number,
    drfStartingBalance: number,
    maxInterestRate: number,
    interestObligations: Map<number, number>,
    _cumulativeDrfAdjustment: number // Reserved for future DRF projection improvements
  ): MinConstraintResult {
    // Use the starting DRF balance as the buffer for constraint calculation.
    // Note: More sophisticated DRF projection was attempted but proved too restrictive.
    // The fiscal year grouping approach handles most cases correctly.
    const effectiveDrfBuffer = drfStartingBalance;

    // Special handling for $0 DRF at first payment (winter tax scenario)
    // With no DRF buffer, the first principal payment can only allocate what
    // its own revenue can support - it cannot rely on later I/O payments to
    // "make up" the difference since there's no buffer to bridge the gap.
    if (currentIndex === 0 && drfStartingBalance === 0 && maxInterestRate > 0) {
      const firstCf = cashFlows[0];
      if (firstCf.isActive && !firstCf.isInterestOnly) {
        // For principal payments with $0 DRF, limit to available at this payment
        // The constraint must ensure: principal + interest ≤ available
        // So: principal * (1 + rate/2) ≤ available  (assuming semi-annual interest)
        // Therefore: principal ≤ available / (1 + rate/2)
        const obligation = interestObligations.get(0) || 0;
        const availableForNewBonds = firstCf.revenueMinusExistingDebt - obligation;
        const safetyBuffer = DISTRIBUTION_CONSTRAINTS.MINIMUM_PAYMENT_SAFETY_BUFFER;
        const netAvailable = availableForNewBonds - safetyBuffer;

        let constraint: number;
        if (netAvailable <= 0) {
          constraint = 0;
        } else {
          // Principal + interest on that principal must fit within netAvailable
          // Assuming semi-annual: constraint = netAvailable / (1 + rate/2)
          constraint = netAvailable / (1 + maxInterestRate / 2);
        }

        return {
          constraint,
          minAvailable: availableForNewBonds,
          minPaymentIndex: 0,
          minPaymentDate: firstCf.paymentDate,
          drfStartingBalance,
          projectedDrfAtConstraint: 0,
        };
      }
    }

    if (currentIndex === 0 || maxInterestRate <= 0) {
      return {
        constraint: Infinity,
        minAvailable: Infinity,
        minPaymentIndex: null,
        minPaymentDate: null,
        drfStartingBalance,
        projectedDrfAtConstraint: effectiveDrfBuffer,
      };
    }

    // Group payments by fiscal year and calculate combined effectiveAvailable
    // This properly handles fiscal year pairs regardless of I/O ordering
    const fiscalYearGroups = new Map<
      string,
      { totalAvailable: number; payments: { index: number; available: number }[] }
    >();

    for (let j = 0; j < currentIndex; j++) {
      const cf = cashFlows[j];
      if (!cf.isActive) continue;

      const fiscalYearKey = cf.relatedFiscalYearEndingDate;
      const obligation = interestObligations.get(j) || 0;
      const effectiveAvailable = cf.revenueMinusExistingDebt - obligation;

      if (!fiscalYearGroups.has(fiscalYearKey)) {
        fiscalYearGroups.set(fiscalYearKey, { totalAvailable: 0, payments: [] });
      }

      const group = fiscalYearGroups.get(fiscalYearKey)!;
      group.totalAvailable += effectiveAvailable;
      group.payments.push({ index: j, available: effectiveAvailable });
    }

    // Find the fiscal year with minimum combined effectiveAvailable
    let minAvailable = Infinity;
    let minPaymentIndex: number | null = null;

    for (const [, group] of fiscalYearGroups) {
      if (group.totalAvailable < minAvailable) {
        minAvailable = group.totalAvailable;
        // Use the last payment in the group as the representative index
        // (typically the final fiscal year payment)
        const lastPayment = group.payments[group.payments.length - 1];
        minPaymentIndex = lastPayment.index;
      }
    }

    if (minAvailable === Infinity) {
      return {
        constraint: Infinity,
        minAvailable: Infinity,
        minPaymentIndex: null,
        minPaymentDate: null,
        drfStartingBalance,
        projectedDrfAtConstraint: effectiveDrfBuffer,
      };
    }

    const safetyBuffer = DISTRIBUTION_CONSTRAINTS.MINIMUM_PAYMENT_SAFETY_BUFFER;
    // Use effective DRF buffer (reduced by consumption from already-processed payments)
    const maxAdditionalInterest = minAvailable + effectiveDrfBuffer - safetyBuffer;

    let constraint: number;
    if (maxAdditionalInterest <= 0) {
      constraint = 0;
    } else {
      constraint = (maxAdditionalInterest * 2) / maxInterestRate;
    }

    return {
      constraint,
      minAvailable,
      minPaymentIndex,
      minPaymentDate: minPaymentIndex !== null ? cashFlows[minPaymentIndex].paymentDate : null,
      drfStartingBalance,
      projectedDrfAtConstraint: effectiveDrfBuffer,
    };
  }

  /**
   * Pure version of helper map building.
   */
  private static buildHelperMapPure(
    cf: CashFlow,
    revenueMap: RevenueMap,
    revenueAvailable: number,
    bondSeries: readonly BondSeries[],
    cumulativePrincipal: Map<string, number>,
    cashFlows: readonly CashFlow[],
    currentIndex: number
  ): RevenueDistributionHelperMap {
    const helperMap: RevenueDistributionHelperMap = {};

    if (cf.isInterestOnly || revenueAvailable < PRINCIPAL_AMOUNTS.MINIMUM_PRINCIPAL) {
      return helperMap;
    }

    // Initialize helper map for each bond
    for (const bp of cf.bondPayments) {
      const bondId = bp.bondSeriesId;
      const series = bondSeries.find((bs) => bs.id === bondId);
      const currentPrincipal = cumulativePrincipal.get(bondId) || 0;

      const targetInfo = series?.targetInformation;
      const hasTarget = !!targetInfo;
      const targetAmount = targetInfo?.principalTargetAmount || 0;
      const remainingCapacity = hasTarget ? Math.max(0, targetAmount - currentPrincipal) : Infinity;

      helperMap[bondId] = {
        isActive: bp.isActive,
        minimumRevenueCapacity: Infinity,
        revenueDistributionPercent: 0,
        principalDistribution: 0,
        hasTarget,
        targetAmount,
        currentPrincipal,
        remainingTargetCapacity: remainingCapacity,
      };
    }

    // Apply distribution strategy
    DistributionStrategyManager.distributeRevenue(
      helperMap,
      revenueMap,
      cf.relatedTaxBillYear,
      revenueAvailable
    );

    // Apply first payment target overrides
    RevenueDistributor.applyFirstPaymentOverridesPure(
      helperMap,
      cf,
      cashFlows,
      currentIndex,
      bondSeries,
      cumulativePrincipal
    );

    return helperMap;
  }

  /**
   * Pure version of first payment target overrides.
   */
  private static applyFirstPaymentOverridesPure(
    helperMap: RevenueDistributionHelperMap,
    cf: CashFlow,
    cashFlows: readonly CashFlow[],
    currentIndex: number,
    bondSeries: readonly BondSeries[],
    cumulativePrincipal: Map<string, number>
  ): void {
    for (const bp of cf.bondPayments) {
      if (!bp.isActive) continue;

      const bond = bondSeries.find((bs) => bs.id === bp.bondSeriesId);
      if (!bond?.targetInformation?.principalTargetAmount) continue;

      // Check if first payment
      const hasEarlierPayment = cashFlows.slice(0, currentIndex).some((earlierCf) => {
        const earlierBp = earlierCf.bondPayments.find((p) => p.bondSeriesId === bp.bondSeriesId);
        return earlierBp?.isActive && !earlierCf.isInterestOnly;
      });

      if (!hasEarlierPayment) {
        // Override with target gap
        const alreadyAllocated = cumulativePrincipal.get(bp.bondSeriesId) || 0;
        const gap = Math.max(0, bond.targetInformation.principalTargetAmount - alreadyAllocated);
        const entry = helperMap[bp.bondSeriesId];
        if (entry) {
          entry.principalDistribution = roundPrincipalDown(gap);
        }
      }
    }
  }

  /**
   * Pure calculation of bond allocations including interest.
   */
  private static calculateBondAllocationsPure(
    cf: CashFlow,
    nextCf: CashFlow | null,
    bondSeries: readonly BondSeries[],
    helperMap: RevenueDistributionHelperMap,
    bondFutureObligations: Map<string, Map<number, number>>,
    paymentIndex: number
  ): BondAllocation[] {
    const allocations: BondAllocation[] = [];

    for (const bp of cf.bondPayments) {
      const helperEntry = helperMap[bp.bondSeriesId];
      const series = bondSeries.find((bs) => bs.id === bp.bondSeriesId);

      if (!bp.isActive || !series) {
        continue;
      }

      const principal = helperEntry?.principalDistribution || 0;

      // Get interest rate for this payment
      const interestRate = InterestCalculationEngine.getInterestRateForPayment(
        series,
        cf.paymentDate
      );

      // Get future obligation from tracking map
      const bondObligationMap = bondFutureObligations.get(bp.bondSeriesId);
      const futureObligation = bondObligationMap?.get(paymentIndex) || 0;

      // Determine start date for interest calculation
      const startDate = InterestCalculationEngine.determineInterestStartDate(
        bp.bondSeriesId,
        cf.paymentDate,
        nextCf,
        series
      );

      // Get payment map data for efficient calculation
      const paymentMapData = InterestCalculationEngine.getPaymentMapData(series, cf.paymentDate);

      // Calculate interest
      const interestResult = InterestCalculationEngine.calculateBondPaymentInterestEfficient(
        principal,
        interestRate,
        startDate,
        cf.paymentDate,
        futureObligation,
        paymentMapData.daysToPrior
      );

      allocations.push({
        bondSeriesId: bp.bondSeriesId,
        principal,
        interestRate,
        newInterest: interestResult.newInterest,
        futureInterestObligation: futureObligation,
        totalInterest: interestResult.totalInterest,
        totalPayment: principal + interestResult.totalInterest,
      });
    }

    return allocations;
  }

  /**
   * Pure cascading of interest to earlier payments.
   */
  private static cascadeInterestPure(
    allocations: readonly BondAllocation[],
    targetCf: CashFlow,
    targetIndex: number,
    bondSeries: readonly BondSeries[],
    interestObligations: Map<number, number>,
    bondFutureObligations: Map<string, Map<number, number>>
  ): void {
    let totalObligationToAdd = 0;

    for (const alloc of allocations) {
      if (alloc.totalInterest === 0) continue;

      const series = bondSeries.find((bs) => bs.id === alloc.bondSeriesId);
      if (!series) continue;

      // Check if target has this bond active
      const targetBp = targetCf.bondPayments.find(
        (bp) => bp.bondSeriesId === alloc.bondSeriesId && bp.isActive
      );
      if (!targetBp) continue;

      // Calculate cascade amount (may be prorated for first payment)
      let cascadeAmount = alloc.totalInterest;

      // Apply first payment proration
      if (targetCf.paymentDate === series.firstInterestPaymentDate) {
        const actualDays = InterestCalculationEngine.days360(
          series.issueDate,
          series.firstInterestPaymentDate
        );
        const standardDays = TIME_PERIODS.STANDARD_PERIOD_DAYS;
        cascadeAmount = Math.round(cascadeAmount * (actualDays / standardDays));
      }

      // Update per-bond obligation tracking (REPLACE, not accumulate!)
      // The chain is maintained because each source's totalInterest
      // already includes the chain from later sources
      const bondMap = bondFutureObligations.get(alloc.bondSeriesId);
      if (bondMap) {
        bondMap.set(targetIndex, cascadeAmount);
      }

      totalObligationToAdd += cascadeAmount;
    }

    // Update total interest obligation (REPLACE with sum of all bonds from this source)
    interestObligations.set(targetIndex, totalObligationToAdd);
  }

  /**
   * Pure update of revenue map.
   */
  private static updateRevenueMapPure(
    revenueMap: RevenueMap,
    cf: CashFlow,
    allocations: readonly BondAllocation[]
  ): void {
    const taxYear = cf.relatedTaxBillYear;

    // Get all years before current
    const yearsToUpdate = Object.keys(revenueMap)
      .map(Number)
      .filter((year) => year < taxYear)
      .sort((a, b) => a - b);

    for (const year of yearsToUpdate) {
      const record = revenueMap[year];
      if (!record) continue;

      // Calculate total interest from active allocations
      const totalInterest = allocations
        .filter((a) => a.totalInterest > 0)
        .reduce((sum, a) => sum + a.totalInterest, 0);

      // Update bond series details
      for (const alloc of allocations) {
        if (alloc.totalInterest > 0) {
          const bondDetails = record.bondSeriesDetailsByBondId[alloc.bondSeriesId] || {
            isActive: true,
            accumulatedInterest: 0,
          };
          bondDetails.accumulatedInterest += alloc.totalInterest;
          record.bondSeriesDetailsByBondId[alloc.bondSeriesId] = bondDetails;
        }
      }

      // Update totals
      record.totalAccumulatedInterest = totalInterest;
      record.availableRevenue = record.revenueMinusExistingDebt - record.totalAccumulatedInterest;

      // Calculate years left for capacity
      const firstYear = parseInt(Object.keys(revenueMap).sort().shift() || '0', 10);
      const yearsLeft = taxYear - firstYear + 1;
      record.revenueCapacity = Math.round(record.availableRevenue / yearsLeft);
    }
  }

  // ============================================================================
  // APPLY FUNCTIONS (Phase 3b - explicit mutation points)
  // ============================================================================

  /**
   * Applies a distribution plan to a candidate's cash flows.
   * This is the explicit mutation point - separates calculation from side effects.
   *
   * @param candidate - The candidate to update
   * @param plan - The distribution plan from calculateDistributionPlan()
   * @param bondSeries - Bond series configuration (for metadata)
   */
  static applyDistributionPlan(
    candidate: Candidate,
    plan: DistributionPlan,
    bondSeries: readonly BondSeries[]
  ): void {
    const { cashFlows } = candidate;

    for (const dist of plan.distributions) {
      const cf = cashFlows[dist.paymentIndex];
      if (!cf) continue;

      // Apply interest obligation from plan
      cf.interestObligation = plan.interestObligations.get(dist.paymentIndex) || 0;
      cf.availableForBonds = cf.revenueMinusExistingDebt - cf.interestObligation;

      // Apply bond allocations
      for (const alloc of dist.bondAllocations) {
        const bp = cf.bondPayments.find((p) => p.bondSeriesId === alloc.bondSeriesId);
        if (!bp) continue;

        const series = bondSeries.find((bs) => bs.id === alloc.bondSeriesId);

        // Apply allocation values
        bp.principal = alloc.principal;
        bp.interestRate = alloc.interestRate;
        bp.newInterest = alloc.newInterest;
        bp.futureInterestObligation = alloc.futureInterestObligation;
        bp.totalInterest = alloc.totalInterest;
        bp.totalPayment = alloc.totalPayment;

        // Apply metadata from series
        if (series) {
          bp.bondCFIssueDate = series.issueDate;
        }
        bp.isInterestOnly = cf.isInterestOnly;
      }

      // Apply totals
      cf.totalBondPayment = dist.totalBondPayment;
    }
  }

  /**
   * Updates revenue map based on a distribution plan.
   * This recalculates the accumulated interest and capacity values.
   *
   * @param revenueMap - The revenue map to update
   * @param plan - The distribution plan
   */
  static updateRevenueMapFromPlan(revenueMap: RevenueMap, plan: DistributionPlan): void {
    // Process distributions in reverse order (backwards through time)
    // to match the original update pattern
    const reversedDists = [...plan.distributions].reverse();

    for (const dist of reversedDists) {
      // Skip distributions with no allocations
      if (dist.bondAllocations.length === 0) continue;

      // Use the actual relatedTaxBillYear from the distribution
      const taxYear = dist.relatedTaxBillYear;

      // Get all years before the tax year
      const yearsToUpdate = Object.keys(revenueMap)
        .map(Number)
        .filter((year) => year < taxYear)
        .sort((a, b) => a - b);

      for (const year of yearsToUpdate) {
        const record = revenueMap[year];
        if (!record) continue;

        // Calculate total interest from allocations
        const totalInterest = dist.bondAllocations
          .filter((a) => a.totalInterest > 0)
          .reduce((sum, a) => sum + a.totalInterest, 0);

        // Update bond series details
        for (const alloc of dist.bondAllocations) {
          if (alloc.totalInterest > 0) {
            const bondDetails = record.bondSeriesDetailsByBondId[alloc.bondSeriesId] || {
              isActive: true,
              accumulatedInterest: 0,
            };
            bondDetails.accumulatedInterest += alloc.totalInterest;
            record.bondSeriesDetailsByBondId[alloc.bondSeriesId] = bondDetails;
          }
        }

        // Update totals
        record.totalAccumulatedInterest = totalInterest;
        record.availableRevenue = record.revenueMinusExistingDebt - record.totalAccumulatedInterest;

        // Calculate years left for capacity
        const firstYear = parseInt(Object.keys(revenueMap).sort().shift() || '0', 10);
        const yearsLeft = taxYear - firstYear + 1;
        record.revenueCapacity = Math.round(record.availableRevenue / yearsLeft);
      }
    }
  }
}
